import { Component, EventEmitter, Input, Output, HostListener, OnInit, OnDestroy, ViewChild, ElementRef, AfterViewInit, ChangeDetectorRef, OnChanges, SimpleChanges } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { HistoryService } from '../history.service';
import { ThemeService } from '../theme.service';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
  standalone: true,
  imports: [CommonModule]
})
export class SidebarComponent implements OnInit, AfterViewInit, OnDestroy, OnChanges {
  @Input() isOpen: boolean = true;
  @Output() toggleSidebar = new EventEmitter<void>();
  @Output() newChat = new EventEmitter<void>();
  @Output() chatSelected = new EventEmitter<string>();
  @ViewChild('sidebar', { static: false }) sidebarElement!: ElementRef;
  
  conversations: any[] = [];
  selectedChatId: string | null = null;
  selectedMoreOptionsId: string | null = null;
  dropdownPosition = { top: 0, left: 0 };
  isMenuHovered: boolean = false;
  private savedScrollPosition: number = 0;
  hasHeaderShadow: boolean = false;

  private historySubscription = new Subscription();
  private routeSubscription!: Subscription;
  private sidebarScrollListener?: () => void;

  constructor(
    private historyService: HistoryService,
    private router: Router,
    private route: ActivatedRoute,
    private cdr: ChangeDetectorRef,
    public themeService: ThemeService
  ) {
    this.conversations = this.historyService.getConversations();
  }

  get isDark(): boolean { return this.themeService.isDark; }

  toggleTheme(): void { this.themeService.toggle(); }

  ngOnInit(): void {
    this.routeSubscription = this.route.queryParams.subscribe(params => {
      this.selectedChatId = params['chatId'] || null;
    });

    this.historySubscription.add(
      this.historyService.historyUpdated.subscribe(() => {
        this.conversations = this.historyService.getConversations();
        console.log('History section conversations:', this.conversations);
      })
    );
  }

  ngAfterViewInit(): void {
    if (this.sidebarElement) {
      this.sidebarScrollListener = () => {
        this.closeDropdownOnScroll();
        this.checkScrollPosition();
      };
      this.sidebarElement.nativeElement.addEventListener('scroll', this.sidebarScrollListener);
    }
  }

  openConversation(id: string): void {
    if (this.selectedChatId === id) return;
  
    this.selectedChatId = id;
    this.router.navigate(['/general-questions'], { queryParams: { chatId: id } });
    this.chatSelected.emit(id);
  }

  onNewChat(): void {  
    this.selectedChatId = null;
    this.newChat.emit();
    this.router.navigate(['/general-questions'], { queryParams: {} });
  }
  
  deleteConversation(chatId: string, event: Event): void {
    event.stopPropagation();
    this.historyService.deleteConversation(chatId);
    this.conversations = this.historyService.getConversations();
    this.selectedMoreOptionsId = null;

    if (this.selectedChatId === chatId) {
      this.router.navigate(['/general-questions'], { queryParams: { chatId: null } });
      this.selectedChatId = null;
    }
  }

  toggleMoreOptions(conversationId: string, event: MouseEvent): void {
    event.stopPropagation();
  
    if (this.selectedMoreOptionsId === conversationId) {
      this.selectedMoreOptionsId = null;
    } else {
      this.selectedMoreOptionsId = conversationId;
  
      const rect = (event.target as HTMLElement).getBoundingClientRect();
      const isMobile = window.innerWidth <= 768;
      
      // Check if popup would be cut off by bottom of screen
      const dropdownHeight = 64; // Height of the dropdown
      const spaceBelow = window.innerHeight - rect.bottom;
      const shouldShowAbove = spaceBelow < dropdownHeight;
      
      this.dropdownPosition = {
        top: shouldShowAbove 
          ? rect.top + window.scrollY - 64  // Above the button
          : rect.bottom + window.scrollY,   // Below the button
        left: isMobile ? 26 : rect.left + window.scrollX
      };
    }
  
    this.cdr.detectChanges();
  }
  
  closeDropdownOnScroll(): void {
    this.selectedMoreOptionsId = null;
  }

  @HostListener('document:click', ['$event'])
  handleClickOutside(event: MouseEvent) {
    if (!(event.target as HTMLElement).closest('.more-icon, .dropdown-container')) {
      this.selectedMoreOptionsId = null;
    }
  }

  @HostListener('window:resize')
  onResize(): void {
    this.selectedMoreOptionsId = null;
  }

  ngOnDestroy(): void {
    this.historySubscription.unsubscribe();
    this.routeSubscription.unsubscribe();

    if (this.sidebarScrollListener && this.sidebarElement) {
      this.sidebarElement.nativeElement.removeEventListener('scroll', this.sidebarScrollListener);
    }
  }

  refreshPage() {
    window.location.reload();
  }

  onMenuMouseEnter(): void {
    this.isMenuHovered = true;
  }

  onMenuMouseLeave(): void {
    this.isMenuHovered = false;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen']) {
      this.isMenuHovered = false;
      
      if (this.sidebarElement) {
        if (this.isOpen) {
          // Restore scroll position when opening
          setTimeout(() => {
            this.sidebarElement.nativeElement.scrollTop = this.savedScrollPosition;
          }, 0);
        } else {
          // Save scroll position when closing
          this.savedScrollPosition = this.sidebarElement.nativeElement.scrollTop;
          this.resetSidebarScroll();
        }
      }
    }
  }

  private resetSidebarScroll(): void {
    if (this.sidebarElement) {
      this.sidebarElement.nativeElement.scrollTop = 0;
    }
  }

  private checkScrollPosition(): void {
    if (this.sidebarElement) {
      const scrollTop = this.sidebarElement.nativeElement.scrollTop;
      this.hasHeaderShadow = scrollTop > 0;
    }
  }
}